import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useCallback,
} from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { reportReelReady } from "../../State/actions";
import { selectHoldAndWinSymbolsReady } from "../../State/selectors";
import HoldAndWinSymbol from "./HoldAndWinSymbol";
import { useDimensions } from "../../Contexts/DimensionsContext";

const HoldAndWinReel = forwardRef(
  (
    {
      reel,
      index,
      symbolWidth,
      symbolHeight,
      reelSpacing,
      symbolsSpriteSheet,
      symbolsSpriteSheetBlurred,
      reelSymbolsData,
      onSymbolStopComplete,
      holdAndWinReelRefs,
    },
    ref
  ) => {
    const symbolRefs = useRef([]);
    const reportedReadyRef = useRef(false);
    const dispatch = useDispatch();
    const holdAndWinSymbolsReady = useSelector(selectHoldAndWinSymbolsReady);
    const { dividerDimensions } = useDimensions();

    // Memoize vertical spacing calculation
    const verticalSpacing = useMemo(
      () => dividerDimensions.width * 1.3,
      [dividerDimensions.width]
    );

    // Memoize container name
    const containerName = useMemo(
      () => `hold_and_win_reel_${index}`,
      [index]
    );

    // Memoize container X position
    const containerX = useMemo(
      () => reelSpacing * (index - 2),
      [reelSpacing, index]
    );

    // Initialize symbol refs array
    useEffect(() => {
      if (symbolRefs.current.length !== reel.length) {
        symbolRefs.current = Array.from({ length: reel.length }, () =>
          React.createRef()
        );
      }
    }, [reel.length]);

    // Memoize controller methods
    const getSymbolRef = useCallback((symbolIndex) => {
      return symbolRefs.current[symbolIndex];
    }, []);

    const getSymbolRefs = useCallback(() => {
      return symbolRefs.current;
    }, []);

    // 🎛️ Expose imperative API - direct access to symbol refs
    useImperativeHandle(ref, () => ({
      getSymbolRef,
      getSymbolRefs,
    }), [getSymbolRef, getSymbolRefs]);

    // Report to Redux when this reel's refs are ready
    // Wait for all symbols to be ready first, then check if refs are populated
    useEffect(() => {
      if (!holdAndWinSymbolsReady || reportedReadyRef.current) {
        return;
      }

      // Check if all symbol refs are populated
      const allRefsPopulated = symbolRefs.current.length === reel.length &&
        symbolRefs.current.every(ref => ref && ref.current);

      if (allRefsPopulated) {
        dispatch(reportReelReady());
        reportedReadyRef.current = true;
      }
    }, [holdAndWinSymbolsReady, reel.length, index, dispatch]);

    return (
      <Container
        name={containerName}
        x={containerX}
      >
        {reel.map((symbol, arrayIdx) => (
          <HoldAndWinSymbol
            key={`symbol-${arrayIdx}`}
            ref={symbolRefs.current[arrayIdx]}
            index={arrayIdx}
            reelIndex={index}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            symbolId={symbol}
            reelSymbolsData={reelSymbolsData}
            onSymbolStop={onSymbolStopComplete}
            symbolsSpriteSheet={symbolsSpriteSheet}
            symbolsSpriteSheetBlurred={symbolsSpriteSheetBlurred}
            holdAndWinReelRefs={holdAndWinReelRefs}
            y={arrayIdx * verticalSpacing}
          />
        ))}
      </Container>
    );
  }
);

export default HoldAndWinReel;
